import PROJECTS_EN from '../api/cv/data/projects.en.json';
import PROJECTS_NL from '../api/cv/data/projects.nl.json';

type IndustryProjectShape = {
  industry?: string;
};

const DEFAULT_INDUSTRY = 'Professional Services';

const getIndustriesFromProjects = (): string[] => {
  const allProjects = [...PROJECTS_EN, ...PROJECTS_NL] as IndustryProjectShape[];
  const industries = new Set<string>();

  allProjects.forEach((project) => {
    if (project.industry && project.industry.trim() !== '') {
      industries.add(project.industry);
    }
  });

  if (industries.size === 0) {
    industries.add(DEFAULT_INDUSTRY);
  }

  return Array.from(industries).sort((left, right) =>
    left.localeCompare(right)
  );
};

export const INDUSTRIES: string[] = getIndustriesFromProjects();

export type Industry = string;
export const isKnownIndustry = (value: string): boolean =>
  INDUSTRIES.includes(value);
export const getDefaultIndustry = (): Industry =>
  INDUSTRIES[0] || DEFAULT_INDUSTRY;
export type FocusArea = string;

/** Fixed profile data sourced from the backend API — not editable by the recruiter. */
export interface CandidateProfile {
  fullName: string;
  title: string;
  description: string;
  certifications: string[];
}

/** What a recruiter or vacancy customizes when generating a tailored CV. */
export interface CvGenerationRequest {
  targetRole: string;
  industries: Industry[];
  focusAreas: FocusArea[];
  technologies: string[];
  includeCertifications: boolean;
  startDate: string;
}

export interface CvProject {
  title: string;
  industry: Industry;
  startedAt: string;
  completedAt: string;
  impact: string;
  technologies: string[];
  focusAreas: FocusArea[];
}

export interface CvGenerationResponse {
  profile: CandidateProfile;
  generatedAt: string;
  headline: string;
  summary: string;
  skills: string[];
  certifications: string[];
  projects: CvProject[];
  markdown: string;
  request: CvGenerationRequest;
}

const unique = (items: string[]): string[] => Array.from(new Set(items));

const isWithinDateRange = (completedAt: string, startDate: string): boolean => {
  // If completedAt is empty or null, treat as ongoing project (always matches)
  if (!completedAt || completedAt.trim() === '') {
    return true;
  }

  const completed = new Date(`${completedAt}T00:00:00`);
  const start = new Date(`${startDate}T00:00:00`);
  const today = new Date();
  const todayAtMidnight = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );

  if (Number.isNaN(completed.getTime()) || Number.isNaN(start.getTime())) {
    return true;
  }

  return completed >= start && completed <= todayAtMidnight;
};

const rankProjects = (
  availableProjects: CvProject[],
  industries: Industry[],
  focusAreas: FocusArea[],
  technologies: string[],
  startDate: string
): CvProject[] => {
  const filteredProjects = availableProjects.filter((project) => {
    const industryMatch =
      industries.length === 0 || industries.includes(project.industry);
    const focusMatch =
      focusAreas.length === 0
      || project.focusAreas.some((focus) => focusAreas.includes(focus));
    const technologyMatch =
      technologies.length === 0
      || project.technologies.some((tech) => technologies.includes(tech));

    return (
      industryMatch
      && focusMatch
      && technologyMatch
      && isWithinDateRange(project.completedAt, startDate)
    );
  });

  const scored = filteredProjects.map((project, index) => {
    const industryScore = industries.includes(project.industry) ? 3 : 0;
    const focusScore =
      project.focusAreas.filter((focus: FocusArea) =>
        focusAreas.includes(focus)
      ).length * 2;
    const techScore =
      technologies.length > 0
        ? project.technologies.filter((tech) =>
            technologies.includes(tech)
          ).length * 1
        : 0;
    const score = industryScore + focusScore + techScore;
    return { ...project, score, index };
  });

  scored.sort((left, right) => {
    // Ongoing projects (null/empty completedAt) always come first
    const leftIsOngoing = !left.completedAt || left.completedAt.trim() === '';
    const rightIsOngoing = !right.completedAt || right.completedAt.trim() === '';

    if (leftIsOngoing && !rightIsOngoing) return -1;
    if (!leftIsOngoing && rightIsOngoing) return 1;

    // Both ongoing or both completed - sort by completedAt descending (most recent first)
    if (!leftIsOngoing && !rightIsOngoing) {
      const leftCompleted = new Date(`${left.completedAt}T00:00:00`).getTime();
      const rightCompleted = new Date(`${right.completedAt}T00:00:00`).getTime();
      
      if (leftCompleted !== rightCompleted) {
        return rightCompleted - leftCompleted;
      }
    }

    // Secondary sort: by startedAt descending
    const leftStarted = new Date(`${left.startedAt}T00:00:00`).getTime();
    const rightStarted = new Date(`${right.startedAt}T00:00:00`).getTime();
    return rightStarted - leftStarted;
  });

  return scored
    .map((project) => ({
      title: project.title,
      industry: project.industry,
      startedAt: project.startedAt,
      completedAt: project.completedAt,
      impact: project.impact,
      technologies: [...project.technologies],
      focusAreas: [...project.focusAreas],
    }));
};

const buildSummary = (
  profile: CandidateProfile,
  request: CvGenerationRequest,
  projects: CvProject[]
): string => {
  if (profile.description && profile.description.trim() !== '') {
    return profile.description.trim();
  }
  // Fallback if no description is provided
  return "No summary available.";
};

const formatMarkdown = (
  response: Omit<CvGenerationResponse, 'markdown'>
): string => {
  const skillLines = response.skills.map((skill) => `- ${skill}`).join('\n');
  const projectLines = response.projects
    .map(
      (project) =>
        `- **${project.title}** (${project.industry}, ${project.completedAt || 'Ongoing'}): ${project.impact}\n  - Tech: ${project.technologies.join(
          ', '
        )}`
    )
    .join('\n');
  const certificationSection =
    response.certifications.length > 0
      ? `\n## Certifications\n${response.certifications
          .map((certification) => `- ${certification}`)
          .join('\n')}\n`
      : '\n## Certifications\n- Not included in this generation request.\n';

  return `# ${response.profile.fullName}
${response.headline}

## Professional Summary
${response.summary}

## Core Skills
${skillLines}

## Project Highlights
${projectLines}${certificationSection}
## Generation Metadata
- Generated At: ${response.generatedAt}
- Target Role: ${response.request.targetRole}
- Industries: ${response.request.industries.join(', ')}
- Focus Areas: ${response.request.focusAreas.join(', ')}
- Project Start Date: ${response.request.startDate}
`;
};

export const generateCv = (
  input: CvGenerationRequest,
  profile: CandidateProfile,
  availableProjects: CvProject[]
): CvGenerationResponse => {
  const request: CvGenerationRequest = {
    ...input,
    targetRole: input.targetRole.trim() || profile.title || 'Microsoft 365 Consultant',
    industries: input.industries || [],
    focusAreas: input.focusAreas || [],
    technologies: input.technologies || [],
    startDate: input.startDate || '1970-01-01',
  };

  const projects = rankProjects(
    availableProjects,
    request.industries,
    request.focusAreas,
    request.technologies,
    request.startDate
  );

  const skills = unique(projects.flatMap((project) => project.technologies)).slice(
    0,
    12
  );

  const summary = buildSummary(profile, request, projects);
  const certifications = request.includeCertifications
    ? profile.certifications
    : [];
  const generatedAt = new Date().toISOString();
  const headline = `${request.targetRole}` || `${profile.title}`;
  const responseWithoutMarkdown: Omit<CvGenerationResponse, 'markdown'> = {
    profile,
    generatedAt,
    headline,
    summary,
    skills,
    certifications,
    projects,
    request,
  };

  return {
    ...responseWithoutMarkdown,
    markdown: formatMarkdown(responseWithoutMarkdown),
  };
};

export const cvRequestSchemaExample: CvGenerationRequest = {
  targetRole: 'Microsoft 365 Expert',
  industries: ['Professional Services', 'Healthcare'],
  focusAreas: ['Microsoft 365 Architecture', 'Power Platform Automation'],
  technologies: [],
  includeCertifications: true,
  startDate: '2023-01-01',
};
